import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PertCalculator = () => {
  const [pertData, setPertData] = useState({
    optimistic: '',
    mostLikely: '',
    pessimistic: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPertData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculatePert = () => {
    const { optimistic, mostLikely, pessimistic } = pertData;

    const optimisticNum = parseFloat(optimistic) || 0;
    const mostLikelyNum = parseFloat(mostLikely) || 0;
    const pessimisticNum = parseFloat(pessimistic) || 0;

    if (optimisticNum === 0 || mostLikelyNum === 0 || pessimisticNum === 0) {
      setError('Please enter valid optimistic, most likely, and pessimistic estimates');
      return;
    }

    const expectedTime = (optimisticNum + 4 * mostLikelyNum + pessimisticNum) / 6;
    const standardDeviation = (pessimisticNum - optimisticNum) / 6;

    const newResults = {
      expectedTime,
      standardDeviation
    };

    setResults(newResults);

    setChartData({
      labels: ['Optimistic', 'Most Likely', 'Pessimistic'],
      datasets: [
        {
          label: 'Estimates',
          data: [optimisticNum, mostLikelyNum, pessimisticNum],
          backgroundColor: ['rgba(75, 192, 192, 0.5)', 'rgba(54, 162, 235, 0.5)', 'rgba(255, 99, 132, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1
        }
      ]
    });
  };

  const exampleData = {
    optimistic: 3,
    mostLikely: 5,
    pessimistic: 10,
    expectedTime: 5.5,
    standardDeviation: 1.167
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>PERT Calculator | Estimate Project Duration</title>
        <meta name="description" content="Estimate your project duration with our PERT Calculator. Enter optimistic, most likely, and pessimistic estimates to get the expected time and standard deviation." />
        <meta name="keywords" content="PERT calculator, project management, project duration, optimistic estimate, most likely estimate, pessimistic estimate" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">PERT</div>
            <h1 className="text-3xl font-bold text-gray-800">PERT Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The PERT Calculator helps you estimate the expected duration of a project based on optimistic, most likely, and pessimistic estimates.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Optimistic Estimate (in days):</label>
                <input
                  type="number"
                  id="optimistic"
                  name="optimistic"
                  value={pertData.optimistic}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter optimistic estimate in days"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Most Likely Estimate (in days):</label>
                <input
                  type="number"
                  id="mostLikely"
                  name="mostLikely"
                  value={pertData.mostLikely}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter most likely estimate in days"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pessimistic Estimate (in days):</label>
                <input
                  type="number"
                  id="pessimistic"
                  name="pessimistic"
                  value={pertData.pessimistic}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter pessimistic estimate in days"
                />
              </div>
            </div>

            <button
              onClick={calculatePert}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate PERT
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">PERT Analysis</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Optimistic Estimate</h3>
                    <p className="text-gray-600">{results.optimistic} days</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Most Likely Estimate</h3>
                    <p className="text-gray-600">{results.mostLikely} days</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Pessimistic Estimate</h3>
                    <p className="text-gray-600">{results.pessimistic} days</p>
                  </div>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Days'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Estimates'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Estimates Breakdown',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">PERT Results</h3>
                  <p className="text-gray-600">Expected Time: {results.expectedTime.toFixed(2)} days</p>
                  <p className="text-gray-600">Standard Deviation: {results.standardDeviation.toFixed(3)} days</p>
                </div>
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-6">
                Here's an example calculation using sample data:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Sample PERT Data:</h4>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Optimistic Estimate: {exampleData.optimistic} days</li>
                  <li>Most Likely Estimate: {exampleData.mostLikely} days</li>
                  <li>Pessimistic Estimate: {exampleData.pessimistic} days</li>
                </ul>
                
                <h4 className="font-semibold mt-6 mb-4">Calculations:</h4>
                <ol className="list-decimal pl-8 space-y-4">
                  <li>
                    <p>Expected Time Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Expected Time = (Optimistic + 4 * Most Likely + Pessimistic) / 6 = ({exampleData.optimistic} + 4 * {exampleData.mostLikely} + {exampleData.pessimistic}) / 6 = {exampleData.expectedTime} days</li>
                    </ul>
                  </li>
                  <li>
                    <p>Standard Deviation Calculation:</p>
                    <ul className="list-none pl-8 space-y-2">
                      <li>Standard Deviation = (Pessimistic - Optimistic) / 6 = ({exampleData.pessimistic} - {exampleData.optimistic}) / 6 = {exampleData.standardDeviation} days</li>
                    </ul>
                  </li>
                </ol>
                
                <p className="mt-6 font-semibold">Interpretation:</p>
                <p>In this example, with an optimistic estimate of {exampleData.optimistic} days, a most likely estimate of {exampleData.mostLikely} days, and a pessimistic estimate of {exampleData.pessimistic} days, the expected time for the project is {exampleData.expectedTime} days with a standard deviation of {exampleData.standardDeviation} days.</p>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding PERT <span className="text-blue-600">📊</span></h2>
            <div className="text-gray-600 mb-8">
              <p>PERT (Program Evaluation and Review Technique) is a statistical tool used in project management to estimate the duration of a project. It takes into account optimistic, most likely, and pessimistic estimates to provide a more accurate and reliable project duration estimate. This calculator helps you:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Estimate the expected time for your project</li>
                <li>Calculate the standard deviation to understand the variability in the estimates</li>
                <li>Make more informed decisions about project planning and scheduling</li>
              </ul>
              <p className="italic mt-2">Note: PERT assumes a beta distribution for the estimates, which may not always be the case in real-world scenarios. Use this tool as a guide and consider other factors that may affect project duration.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use PERT Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the PERT Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Enter Your Estimates</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Enter the optimistic estimate for your project</li>
                  <li>Enter the most likely estimate for your project</li>
                  <li>Enter the pessimistic estimate for your project</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Calculate PERT</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Click "Calculate PERT" to generate the results</li>
                  <li>View the expected time and standard deviation for your project</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Interpret Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Use the expected time to plan your project schedule</li>
                  <li>Consider the standard deviation to understand the uncertainty in the estimates</li>
                  <li>Adjust your project plan based on the PERT analysis results</li>
                </ul>
              </div>

              <div className="mt-6 bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Ensure accurate and realistic estimates from your team members</li>
                  <li>Consider other factors that may affect project duration, such as resource availability and external dependencies</li>
                  <li>Use PERT as a guide and combine it with other project management tools and techniques for better results</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PertCalculator;